import { ComponentProps, JSX, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

import { Box } from "@mui/material";
import {
  OtherComponentType,
  OtherComponents,
} from "@plansoft-configuration-renderer/components";

import { useStickyCadContainer } from "@/context/StickyCadContext";

// Original framework viewer, referenced through the component map so we don't
// depend on its (internal) import path.
const OriginalCadClickViewer3D =
  OtherComponents[OtherComponentType.CadClickViewer3D];

type CadClickViewer3DProps = ComponentProps<typeof OriginalCadClickViewer3D>;

// Keeps the viewer clear of the fixed ConfigurationHeader (mirrors the tree).
const HEADER_OFFSET_WIDE_PX = 65;
const HEADER_OFFSET_PX = 105;
const WIDE_BREAKPOINT_PX = 1400;
const BOTTOM_GAP_PX = 16;

// Dialog overlay must clear SubstructureDialog's zIndex 800.
const PAGE_OVERLAY_Z_INDEX = 10;
const DIALOG_OVERLAY_Z_INDEX = 850;

/**
 * Renders the CAD viewer in a `position: fixed` overlay portaled to body so it
 * stays put while content scrolls; a placeholder reserves the column and sets
 * the overlay width.
 * - No container: freeze under the header (window scroll).
 * - Container: pin to the container's box, above it (dialog scroll).
 */
export function CustomCadClickViewer3D(
  props: CadClickViewer3DProps,
): JSX.Element {
  const container = useStickyCadContainer();
  const placeholderRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  const syncPosition = useCallback((): void => {
    const placeholder = placeholderRef.current;
    const overlay = overlayRef.current;
    if (!placeholder || !overlay) {
      return;
    }

    // Horizontal: always follow the placeholder's column.
    const rect = placeholder.getBoundingClientRect();
    overlay.style.left = `${rect.left}px`;
    overlay.style.width = `${rect.width}px`;

    if (container) {
      // Dialog scrolls only its inner content, so its box is a stable anchor.
      const containerRect = container.getBoundingClientRect();
      const top = containerRect.top + BOTTOM_GAP_PX;
      overlay.style.top = `${top}px`;
      overlay.style.height = `${containerRect.bottom - top - BOTTOM_GAP_PX}px`;
      return;
    }

    const headerOffset =
      window.innerWidth >= WIDE_BREAKPOINT_PX
        ? HEADER_OFFSET_WIDE_PX
        : HEADER_OFFSET_PX;

    // Freeze at the scroll-0 document position, clamped below the header.
    const top = Math.max(rect.top + window.scrollY, headerOffset);
    overlay.style.top = `${top}px`;
    overlay.style.height = `${window.innerHeight - top - BOTTOM_GAP_PX}px`;
  }, [container]);

  useEffect(() => {
    syncPosition();

    const placeholder = placeholderRef.current;
    // Track column and container size/position changes.
    const resizeObserver = new ResizeObserver(syncPosition);
    if (placeholder) {
      resizeObserver.observe(placeholder);
    }
    if (container) {
      resizeObserver.observe(container);
    }
    window.addEventListener("resize", syncPosition);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", syncPosition);
    };
  }, [syncPosition, container]);

  const zIndex = container ? DIALOG_OVERLAY_Z_INDEX : PAGE_OVERLAY_Z_INDEX;

  return (
    <>
      {/* Reserves the column space and sets the overlay width. */}
      <Box ref={placeholderRef} sx={{ width: "100%", height: "100%" }} />
      {createPortal(
        <Box
          ref={overlayRef}
          sx={{ position: "fixed", zIndex, overflow: "hidden" }}
        >
          <OriginalCadClickViewer3D {...props} />
        </Box>,
        document.body,
      )}
    </>
  );
}
